import * as React from 'react';
import { FunctionComponent, useEffect, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag } from '@fortawesome/free-solid-svg-icons';
import { UnsavedChangesModal } from '../UnsavedChangesModal/UnsavedChangesModal';
import '../UserTagAssignModal/UserTagActionModal.scss';

interface Props {
  modalTitle: string;
  tournamentTags: any[];
  selectedTournamentTagId: string;
  selectedTournaments?: any[];
  onTournamentTagChange: (event) => void;
  onCloseClick: () => void;
  isOpen: boolean;
  onSaveClick: (selectedTagIds?: string[]) => void;
}

const getTagColor = (tag: any): string => tag.tagColor || '#227ebd';

export const TournamentTagAssignModal: FunctionComponent<Props> = props => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDiscardWarningOpen, setIsDiscardWarningOpen] = useState(false);

  useEffect(() => {
    if (props.isOpen) {
      setSelectedIds(props.selectedTournamentTagId ? [props.selectedTournamentTagId] : []);
      setIsDiscardWarningOpen(false);
    }
  }, [props.isOpen]);

  if (!props.isOpen) return null;

  const modalClasses = classNames('userTagActionModal', {
    mDisplayBlock: props.isOpen,
    show: props.isOpen,
    modal: true,
    fade: true
  });
  const selectedTags = props.tournamentTags.filter(tag => selectedIds.includes(tag.id));
  const availableTags = props.tournamentTags.filter(tag => !selectedIds.includes(tag.id));
  const tournamentCount = (props.selectedTournaments || []).length;
  const toggleTag = (tagId: string) =>
    setSelectedIds(current => (current.includes(tagId) ? current.filter(id => id !== tagId) : [...current, tagId]));
  const requestClose = () => (selectedIds.length ? setIsDiscardWarningOpen(true) : props.onCloseClick());

  return (
    <div className={modalClasses} onMouseDown={event => event.target === event.currentTarget && requestClose()}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Assign tags</h5>
            <button type="button" className="close" onClick={requestClose} aria-label="Close">
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <div className="userTagAction">
              <div className="userTagAction__intro">
                <p>Tags help you group and filter tournaments.</p>
              </div>
              <div className="userTagAction__students">
                <FontAwesomeIcon icon={faTag} />
                <span>
                  Adding tags to {tournamentCount} {tournamentCount === 1 ? 'tournament' : 'tournaments'}.
                </span>
              </div>
              {selectedTags.length > 0 && (
                <div className="userTagAction__selection">
                  <span className="userTagAction__label">Selected tags</span>
                  <div className="userTagAction__chips userTagAction__chips--selected">
                    {selectedTags.map(tag => (
                      <button
                        key={tag.id}
                        type="button"
                        className="userTagAction__chip"
                        data-selected="true"
                        style={{ color: '#fff', backgroundColor: getTagColor(tag) }}
                        onClick={() => toggleTag(tag.id)}
                      >
                        <span className="userTagAction__dot" />
                        {tag.tagName}
                        <span aria-hidden="true">×</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <span className="userTagAction__label">Available tags</span>
                {availableTags.length ? (
                  <div className="userTagAction__chips">
                    {availableTags.map(tag => {
                      const color = getTagColor(tag);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          className="userTagAction__chip"
                          data-selected="false"
                          style={{ color, backgroundColor: `${color}1A` }}
                          onClick={() => toggleTag(tag.id)}
                        >
                          <span className="userTagAction__dot" />
                          {tag.tagName}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="userTagAction__empty">No tournament tags are available.</div>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="sit-btn sit-btn--ghost" onClick={requestClose}>
              Cancel
            </button>
            <button
              type="button"
              className="sit-btn sit-btn--primary"
              disabled={selectedIds.length === 0}
              onClick={() => props.onSaveClick(selectedIds)}
            >
              <FontAwesomeIcon icon={faTag} /> Assign tags
            </button>
          </div>
        </div>
      </div>
      <UnsavedChangesModal
        isOpen={isDiscardWarningOpen}
        message="Your tag selection has not been saved. If you close now, those changes will be lost."
        onKeepEditing={() => setIsDiscardWarningOpen(false)}
        onDiscard={() => {
          setIsDiscardWarningOpen(false);
          props.onCloseClick();
        }}
      />
    </div>
  );
};

TournamentTagAssignModal.displayName = 'TournamentTagAssignModal';
